<template>
  <div>
    <v-card>
      <div class="pa-6">
        <div class="d-flex justify-end align-center pb-6">
          <v-spacer></v-spacer>
          <v-btn color="green" dark outlined @click="exportDataToExcel">
            <v-icon left>mdi-file-excel-outline</v-icon>Xuất Excel</v-btn
          >
        </div>

        <v-card outlined>
          <v-data-table
            item-key="id"
            :headers="headers"
            :loading="loading"
            :items="marks"
            :items-per-page="-1"
            :disable-sort="$vuetify.breakpoint.smAndDown"
            hide-default-footer
            v-bind="this.$attrs"
            fixed-header
          >
            <template v-slot:[`item.dob`]="{ item }">
              {{ item.student.dob | ddmmyyyy }}
            </template>
            <template v-slot:[`item.value`]="{ item }">
              <div>
                {{ item.value }}
              </div>
            </template>
          </v-data-table>
        </v-card>
      </div>
    </v-card>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import moment from 'moment'
import XLSX from 'xlsx-js-style'

const originHeaders = [
  {
    text: 'Học sinh',
    value: 'student.name',
    align: 'left',
    sortable: false,
    width: '33%'
  },
  {
    text: 'Ngày sinh',
    value: 'dob',
    align: 'left',
    sortable: false,
    width: '33%'
  },
  {
    text: 'Điểm',
    value: 'value',
    align: 'left',
    sortable: false,
    show: false,
    width: '20%'
  }
]

export default {
  props: {
    query: Object,
    room: Object,
    subject: Object,
    exam: Object
  },
  data() {
    return {
      loading: false,
      headers: originHeaders,
      marks: [],
      item: {},
      importedDocuments: [],
      confirmMarkDialog: false,
      json_fields: {
        STT: 'index',
        'Mã HS': 'student.code',
        'Học sinh': 'student.name',
        'Ngày sinh': 'student.data.dob',
        Điểm: 'value'
      }
    }
  },
  computed: {
    ...mapGetters('auth', ['user'])
  },
  methods: {
    ...mapActions('examMark', ['fetchMarks', 'countMarks']),
    async refresh() {
      this.$loading.active = true
      this.loading = true
      try {
        const fetchedMarks = await this.fetchMarks({
          ...this.query,
          _sort: 'examCode:ASC'
        })
        this.marks = fetchedMarks
      } catch (error) {
        this.$alert.error('Lỗi load thông tin học sinh')
      } finally {
        this.loading = false
        this.$loading.active = false
      }
    },
    formatDataForExport(data) {
      return data.map((item, index) => {
        let formattedItem = {}
        for (let key in this.json_fields) {
          const fieldPath = this.json_fields[key]
          let value = item

          // Traverse the field path to get the value
          if (typeof fieldPath === 'string') {
            const pathParts = fieldPath.split('.')
            pathParts.forEach(part => {
              value = value ? value[part] : '' // Set value to '' if it's undefined/null
            })
          }

          formattedItem[key] = value !== undefined && value !== null ? value : ''
          // Add the STT value based on the index
          if (key === 'STT') {
            value = index + 1 // STT starts from 1
            formattedItem[key] = { v: value, t: 's' }
          }
        }
        return formattedItem
      })
    },
    exportDataToExcel() {
      const formattedData = this.formatDataForExport(this.marks)
      const worksheet = XLSX.utils.json_to_sheet([], { skipHeader: true })
      const headers = [
        ['Trường THCS Lý Thái Tổ', ''],
        [`KẾT QUẢ ${this.exam.name.toUpperCase()}`, ''],
        [`Môn: ${this.subject.title}`, ''],
        [`Lớp: ${this.room.title}`, '']
      ]
      const signatureRow = formattedData.length + 7

      headers.forEach((header, rowIndex) => {
        const cellAddress = `A${rowIndex + 1}`
        worksheet[cellAddress] = { v: header[0], t: 's' }

        if (rowIndex === 0 || rowIndex === 2 || rowIndex === 3) {
          worksheet[cellAddress].s = {
            font: { bold: true, sz: 13, name: 'Times New Roman' },
            alignment: { horizontal: 'left', vertical: 'center' }
          }
        }

        if (rowIndex === 1) {
          worksheet[cellAddress].s = {
            font: { bold: true, sz: 14, name: 'Times New Roman' },
            alignment: { horizontal: 'center', vertical: 'center', wrapText: true }
          }
        }
      })

      // Define merges for the headers
      worksheet['!merges'] = [
        { s: { r: 0, c: 0 }, e: { r: 0, c: 4 } },
        { s: { r: 1, c: 0 }, e: { r: 1, c: 4 } },
        { s: { r: 2, c: 0 }, e: { r: 2, c: 4 } },
        { s: { r: 3, c: 0 }, e: { r: 3, c: 4 } }
      ]
      // Set column widths for columns B and C
      worksheet['!cols'] = [
        { wch: 8 }, // Column A width
        { wch: 17 }, // Column C width
        { wch: 20 }, // Column B width
        { wch: 17 }, // Column D width
        { wch: 10 } // Column E width
      ]
      // Define common styles for headers and data cells
      const headerStyle = {
        font: { bold: true, sz: 12, name: 'Times New Roman' },
        alignment: { horizontal: 'center', vertical: 'center' },
        border: {
          top: { style: 'thin' },
          bottom: { style: 'thin' },
          left: { style: 'thin' },
          right: { style: 'thin' }
        }
      }

      const dataStyle = {
        font: { sz: 12, name: 'Times New Roman' },
        alignment: { horizontal: 'center', vertical: 'center' },
        border: {
          top: { style: 'thin' },
          bottom: { style: 'thin' },
          left: { style: 'thin' },
          right: { style: 'thin' }
        }
      }

      // Add the actual table data starting from A5
      XLSX.utils.sheet_add_json(worksheet, formattedData, {
        origin: 'A5',
        header: this.getHeaders(),
        skipHeader: false
      })

      this.getHeaders().forEach((header, colIndex) => {
        const colLetter = String.fromCharCode(65 + colIndex) // Convert column index to letter (A, B, C, ...)
        const headerCellAddress = `${colLetter}5`

        // Ensure the header cell exists before applying styles
        if (worksheet[headerCellAddress]) {
          worksheet[headerCellAddress].s = headerStyle // Apply header style
        }

        // Apply data style to each data row only if data is available
        if (formattedData.length > 0) {
          for (let rowIndex = 6; rowIndex < formattedData.length + 6; rowIndex++) {
            const dataCellAddress = `${colLetter}${rowIndex}`

            // Ensure the data cell exists before applying styles
            if (worksheet[dataCellAddress]) {
              worksheet[dataCellAddress].s = dataStyle
            }
          }
        }
      })

      // If there is no data, apply the border to just the first row after headers
      if (formattedData.length === 0) {
        this.getHeaders().forEach((header, colIndex) => {
          const colLetter = String.fromCharCode(65 + colIndex) // Convert column index to letter (A, B, C, ...)
          const dataCellAddress = `${colLetter}6`

          worksheet[dataCellAddress] = { v: '', t: 's' }
          worksheet[dataCellAddress].s = headerStyle // Apply header style to the first row after headers
        })
      }

      const endRow = signatureRow + 6
      worksheet['!ref'] = `A1:E${endRow}`
      const workbook = XLSX.utils.book_new()
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Điểm')
      XLSX.writeFile(workbook, `diem-${this.subject.title}-${this.room.title}.xlsx`)
      this.$loading.active = false
    },
    getHeaders() {
      const headers = []
      for (let key in this.json_fields) {
        headers.push(key)
      }
      return headers
    }
  },
  watch: {
    query: {
      immediate: true,
      handler(newVal) {
        if (newVal) {
          this.refresh()
        }
      }
    }
  },
  filters: {
    getDate: item => {
      if (item) return moment(item).format('DD/MM/YYYY')
      return ''
    }
  },
  components: {}
}
</script>
